import Empty from '#/components/Empty';
import Network from '#/components/Network';
import NetworkAccount from '#/components/NetworkAccount';
import NetworkAccountLabel from '#/components/NetworkAccountLabel';
import NetworkUSDValue from '#/components/NetworkUSDValue';
import NetworkValue from '#/components/NetworkValue';
import Skeleton from '#/components/Skeleton';
import TableSortButton from '#/components/TableSortButton';
import { HelpTooltip } from '#/components/v2/help-tooltip';
import USDDecimal from '#/constants/USDDecimal';
import { useTranslation } from '#/contexts/useTranslation';
import useNetworks from '#/data/useNetworks';
import useWatcherPriceList from '#/data/useWatcherPriceList';
import useWatcherAddressList, {
  WatcherAddressInfo,
} from '#/data/v1/useWatcherAddressList';
import { cn } from '#/lib/utils';
import parseUnits from '#/utils/parseUnits';
import {
  ColumnDef,
  Row,
  SortingFns,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ethers } from 'ethers';
import { useCallback, useMemo, useState } from 'react';
import { TableRowActions } from './staking-addresses-table-row-actions';

interface Props {
  symbols?: string[];
}

export default function StakingAddressesTable({ symbols }: Props) {
  const translation = useTranslation(
    translation => translation.PortfolioDashboard.ManageYourAddresses
  );
  const { isLoading, data } = useWatcherAddressList({
    symbols: symbols ?? [],
  });
  const dataSource = useMemo(() => data?.data.data ?? [], [data]);
  const mockDataSource = useMemo(() => Array.from({ length: 6 }), []);

  const { data: networks } = useNetworks();
  const { data: watcherPriceList } = useWatcherPriceList();
  const valueSortingFn = useCallback(
    (
      rowA: Row<WatcherAddressInfo>,
      rowB: Row<WatcherAddressInfo>,
      columnId: string
    ) => {
      const networkA = networks?.data.data?.find(
        item => item.symbol === rowA.original.symbol
      );
      const networkB = networks?.data.data?.find(
        item => item.symbol === rowB.original.symbol
      );
      const priceA = watcherPriceList?.data.data?.find(
        item => item.symbol === rowA.original.symbol
      );
      const priceB = watcherPriceList?.data.data?.find(
        item => item.symbol === rowB.original.symbol
      );
      if (priceA && priceB && networkA && networkB) {
        // @ts-ignore
        const valueA = rowA.original[columnId];
        // @ts-ignore
        const valueB = rowB.original[columnId];

        const result = ethers.BigNumber.from(valueA)
          .mul(parseUnits(priceA.price, USDDecimal))
          .div(parseUnits('1', networkA.decimal))
          .sub(
            ethers.BigNumber.from(valueB)
              .mul(parseUnits(priceB.price, USDDecimal))
              .div(parseUnits('1', networkB.decimal))
          );
        return result.isZero() ? 0 : result.isNegative() ? -1 : 1;
      } else {
        return 0;
      }
    },
    [networks, watcherPriceList]
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useMemo<ColumnDef<WatcherAddressInfo>[]>(
    () => [
      {
        id: 'symbol',
        enableSorting: false,
        cell: info => <Network symbol={info.row.original.symbol} />,
        header: translation.HeadingNetwork,
        footer: props => props.column.id,
      },
      {
        id: 'address',
        enableSorting: false,
        cell: info => (
          <NetworkAccount
            symbol={info.row.original.symbol}
            address={info.row.original.address}
          />
        ),
        header: translation.HeadingAddress,
        footer: props => props.column.id,
      },
      {
        id: 'delegated',
        sortingFn: 'valueSortingFn' as keyof SortingFns,
        accessorFn: row => row.delegated,
        cell: info =>
          sorting.some(item => item.id === info.column.id) ? (
            <dl className="flex flex-col space-y-1">
              <dt className="whitespace-nowrap">
                <NetworkValue
                  symbol={info.row.original.symbol}
                  value={info.row.original.delegated}
                  maxDecimalDigits={5}
                />
              </dt>
              <dd className="text-gray-500 dark:text-gray-400">
                <NetworkUSDValue
                  symbol={info.row.original.symbol}
                  value={info.row.original.delegated}
                />
              </dd>
            </dl>
          ) : (
            <span className="whitespace-nowrap">
              <NetworkValue
                symbol={info.row.original.symbol}
                value={info.row.original.delegated}
                maxDecimalDigits={5}
              />
            </span>
          ),
        header: () => (
          <>
            <span className="align-middle">{translation.HeadingStaked}</span>
            <HelpTooltip content={translation.HeadingStakedHelp} />
          </>
        ),
        footer: props => props.column.id,
      },
      {
        id: 'rewards',
        sortingFn: 'valueSortingFn' as keyof SortingFns,
        accessorFn: row => row.rewards,
        cell: info =>
          sorting.some(item => item.id === info.column.id) ? (
            <dl className="flex flex-col space-y-1">
              <dt className="whitespace-nowrap">
                <NetworkValue
                  symbol={info.row.original.symbol}
                  value={info.row.original.rewards}
                  maxDecimalDigits={5}
                />
              </dt>
              <dd className="text-gray-500 dark:text-gray-400">
                <NetworkUSDValue
                  symbol={info.row.original.symbol}
                  value={info.row.original.rewards}
                />
              </dd>
            </dl>
          ) : (
            <span className="whitespace-nowrap">
              <NetworkValue
                symbol={info.row.original.symbol}
                value={info.row.original.rewards}
                maxDecimalDigits={5}
              />
            </span>
          ),
        header: () => (
          <>
            <span className="align-middle">{translation.HeadingRewards}</span>
            <HelpTooltip content={translation.HeadingRewardsHelp} />
          </>
        ),
        footer: props => props.column.id,
      },
      {
        id: 'name',
        enableSorting: false,
        cell: info => (
          <NetworkAccountLabel
            symbol={info.row.original.symbol}
            address={info.row.original.address}
            label={info.row.original.label}
          />
        ),
        header: translation.HeadingName,
      },
      {
        id: 'operation',
        cell: ({ row }) => <TableRowActions row={row} />,
        enableSorting: false,
        header: translation.HeadingAction,
      },
    ],
    [sorting]
  );
  const table = useReactTable({
    data: dataSource,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: true,
    sortingFns: {
      valueSortingFn,
    },
  });

  return (
    <>
      <div className="bg-white dark:bg-gray-700 rounded-2xl space-y-4">
        <article className="overflow-auto">
          <table className="table-auto w-full text-gray-800 dark:text-gray-200 text-base">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr
                  key={headerGroup.id}
                  className="bg-slate-200 dark:bg-slate-600"
                >
                  {headerGroup.headers.map(header => {
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16"
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={cn(
                              'whitespace-nowrap flex items-center space-x-1 font-medium',
                              {
                                'cursor-pointer select-none':
                                  header.column.getCanSort(),
                              }
                            )}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <TableSortButton
                                direction={header.column.getIsSorted()}
                              />
                            )}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading
                ? mockDataSource.map((_, i) => <SkeletonItem key={i} />)
                : table.getRowModel().rows.map(row => {
                    return (
                      <tr
                        key={row.id}
                        className="even:bg-slate-50 dark:even:bg-slate-600/25"
                      >
                        {row.getVisibleCells().map(cell => {
                          return (
                            <td
                              key={cell.id}
                              className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
            </tbody>
          </table>
          {!isLoading && dataSource.length === 0 && <Empty />}
        </article>
      </div>
    </>
  );
}

function SkeletonItem() {
  return (
    <tr className="even:bg-slate-50 dark:even:bg-slate-600/25">
      <td className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16">
        <Skeleton isLoading className="w-32" />
      </td>
      <td className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16">
        <Skeleton isLoading className="w-32" />
      </td>
      <td className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16">
        <Skeleton isLoading className="w-32" />
      </td>
      <td className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16">
        <Skeleton isLoading className="w-32" />
      </td>
      <td className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16">
        <Skeleton isLoading className="w-12" />
      </td>
      <td className="px-2 py-4 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg h-16">
        <Skeleton isLoading className="w-8 inline-block" />
      </td>
    </tr>
  );
}
